import React, { useState, useEffect, useCallback } from "react";
import {
  Flex, Text, Input, Table, Thead, Tbody, Tr, Th, Td, Badge, Button, useToast,
} from "@chakra-ui/react";
import { Panel } from "../shared/Section";
import { TableSkeleton } from "../shared/LoadingBlock";
import { fetchList, reopenItem } from "./useNewData";

// Month history of the New Data queue (docs/michael/TASK-new-data-tab.md §7 S16):
// everything handled — added / auto_added / ignored / excluded / dc — with who
// did it and a Reopen. status:'resolved' = everything non-pending (S13 /list).

const STATUS_COLORS = {
  added: "green",
  auto_added: "teal",
  ignored: "gray",
  excluded: "orange",
  dc: "purple",
  pending: "yellow",
};

const STATUS_LABELS = {
  added: "Added",
  auto_added: "Added automatically",
  ignored: "Ignored",
  excluded: "Excluded",
  dc: "DC",
  pending: "Pending",
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtWhen = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(date);
};

const byLabel = (row) => {
  if (row.resolved_by_name) return row.resolved_by_name;
  if (row.fname) return row.fname;
  if (row.status === "auto_added") return "system";
  return row.resolved_by ? `user ${row.resolved_by}` : "—";
};

const NewDataHistory = ({ month: initialMonth }) => {
  const toast = useToast();
  const [month, setMonth] = useState(initialMonth || currentMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (initialMonth) setMonth(initialMonth);
  }, [initialMonth]);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const list = await fetchList({ status: "resolved", month: m });
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      toast({
        title: "Failed to load history",
        description: error?.response?.data?.message || "Technical error occurred",
        status: "error", duration: 4000, isClosable: true,
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(month);
  }, [month, load]);

  const reopen = async (row) => {
    try {
      await reopenItem(row.id);
      toast({ title: "Reopened", description: `${row.code} is pending again.`, status: "success", duration: 2500, isClosable: true });
      load(month);
    } catch (error) {
      toast({
        title: "Failed to reopen",
        description: error?.response?.data?.message || "Technical error occurred",
        status: "error", duration: 5000, isClosable: true,
      });
    }
  };

  return (
    <>
      <Flex align="center" gap={3} mb={3}>
        <Text fontWeight="600" fontSize="sm" whiteSpace="nowrap">History</Text>
        <Input type="month" size="sm" width="170px" value={month} onChange={(e) => setMonth(e.target.value)} />
        {!loading && (
          <Text fontSize="sm" color="ink.500" whiteSpace="nowrap">
            {rows.length} {rows.length === 1 ? "item" : "items"}
          </Text>
        )}
      </Flex>

      <Panel padded={false} maxH="480px" overflow="auto">
        {loading ? (
          <TableSkeleton rows={8} minH="240px" />
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom" textStyle="smallText">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                {["When", "Kind", "Retailer", "Code", "Description", "Supplier", "File", "Status", "By", ""].map((h, i) => (
                  <Th key={`${h}-${i}`} px={2} py={1} bg="gray.50">{h}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.length === 0 ? (
                <Tr>
                  <Td colSpan={10} py={6}>
                    <Text fontSize="sm" color="ink.500" textAlign="center">Nothing handled in this month.</Text>
                  </Td>
                </Tr>
              ) : (
                rows.map((row) => (
                  <Tr key={row.id}>
                    <Td px={2} py={1} whiteSpace="nowrap">{fmtWhen(row.resolved_at || row.last_seen_at || row.first_seen_at)}</Td>
                    <Td px={2} py={1}>{row.kind}</Td>
                    <Td px={2} py={1}>{row.retailer}</Td>
                    <Td px={2} py={1} fontWeight="600" whiteSpace="nowrap">{row.raw_code && row.raw_code !== row.code ? `${row.code} (${row.raw_code})` : row.code}</Td>
                    <Td px={2} py={1} maxW="260px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={row.description || ""}>{row.description || "—"}</Td>
                    <Td px={2} py={1}>{row.suppliercode || "—"}</Td>
                    <Td px={2} py={1} maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={row.source_file || ""}>{row.source_file || "—"}</Td>
                    <Td px={2} py={1}>
                      <Badge colorScheme={STATUS_COLORS[row.status] || "gray"}>{STATUS_LABELS[row.status] || row.status}</Badge>
                    </Td>
                    <Td px={2} py={1} whiteSpace="nowrap">{byLabel(row)}</Td>
                    <Td px={2} py={1}>
                      {row.status !== "pending" && (
                        <Button size="xs" variant="ghost" onClick={() => reopen(row)}>Reopen</Button>
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </Panel>
    </>
  );
};

export default NewDataHistory;
